import React, { useState, useEffect } from "react";
import { Building, Eye } from "lucide-react";
import Dropdown from "@/components/ui/Dropdown";
import formLabelService from "@/services/FormLabel";
import { companiesService } from "@/services/companiesService";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import "react-toastify/dist/ReactToastify.css";
import ToggleSwitch from "@/components/ui/ToggleSwitch";
import { useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const [selectedFields, setSelectedFields] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardFieldsId, setDashboardFieldsId] = useState(null);
  const { showToast } = useToastStore;
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem("adminDashboardSelectedCompany");
    return saved ? JSON.parse(saved) : null;
  });
  const [dropdownValues, setDropdownValues] = useState({});
  const [isStageFilterEnabled, setIsStageFilterEnabled] = useState(false);
  const [totalCandidatesScoreEnabled, setTotalCandidatesScoreEnabled] =
    useState(false);
  const [visualizationTypeValues, setVisualizationTypeValues] = useState({});
  const [limitValues, setLimitValues] = useState({});
  const navigate = useNavigate();

  const dropdownOptions = [
    { label: "Option 1", value: "1" },
    { label: "Option 2", value: "2" },
    { label: "Option 3", value: "3" },
  ];

  const visualizationOptions = [
    { label: "Card", value: "card" },
    { label: "Listing Top 10", value: "listing__10" },
    { label: "Listing Top 20", value: "listing__20" },
    { label: "Listing Top 30", value: "listing__30" },
    { label: "Bar Graph", value: "bar" },
    { label: "Line Graph", value: "line" },
    { label: "Pie Graph", value: "pie" },
  ];

  // Data type options for each field type or label
  const fieldDataTypeOptions = {
    "First name": [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
    ],
    "Family Name": [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
    ],
    "Phone Number": [
      { label: "Data type", value: "" },
      { label: "Phone", value: "phone" },
      { label: "Text", value: "text" },
    ],
    "University attended": [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
    ],
    Gender: [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
      { label: "Boolean", value: "boolean" },
    ],
    "Languages spoken": [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
    ],
    Disability: [
      { label: "Data type", value: "" },
      { label: "Boolean", value: "boolean" },
      { label: "Text", value: "text" },
    ],
    "Please read each question carefully": [
      { label: "Data type", value: "" },
      { label: "Boolean", value: "boolean" },
      { label: "Text", value: "text" },
    ],
    // Default fallback
    default: [
      { label: "Data type", value: "" },
      { label: "Text", value: "text" },
      { label: "Number", value: "number" },
      { label: "Boolean", value: "boolean" },
      { label: "Date", value: "date" },
      { label: "Email", value: "email" },
      { label: "Phone", value: "phone" },
    ],
  };

  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        // Fetch all pages of companies
        const perPage = 100;
        let currentPage = 1;
        let aggregated = [];
        let lastPage = 1;

        do {
          const response = await companiesService.getAllCompanies({
            per_page: perPage,
            page: currentPage,
          });
          const pageData = Array.isArray(response?.data) ? response.data : [];
          aggregated = aggregated.concat(pageData);
          lastPage = response?.pagination?.last_page ?? 1;
          currentPage += 1;
        } while (currentPage <= lastPage);

        setCompanies(aggregated);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchPersonalInfoForm = async (companyId = null) => {
      try {
        setLoading(true);
        setError(null);
        const response = await formLabelService.getPersonalInfoForm(companyId);

        // Get fields from the first dashboard_fields item
        const fields = response.dashboard_fields?.[0]?.fields || [];
        const id = response.dashboard_fields?.[0]?.id;

        setFormFields(fields);
        setDashboardFieldsId(id);

        // Initialize selectedFields with isChecked values
        const initialSelectedFields = {};
        const initialVisualizationTypes = {};
        const initialLimitValues = {};

        // Initialize social mobility score field
        initialSelectedFields["sms"] = false; // Default to unchecked
        initialVisualizationTypes["sms"] = null; // Default to no visualization type

        // Initialize total candidates score field
        initialSelectedFields["total_candidates_score"] = false; // Default to unchecked
        initialVisualizationTypes["total_candidates_score"] = null; // Default to no visualization type

        fields.forEach((field) => {
          if (field.isChecked) {
            initialSelectedFields[field.id] = true;
          }
          if (field.visualization_type) {
            initialVisualizationTypes[field.id] = field.visualization_type;
            // Extract limit from visualization type if it's a listing type
            if (field.visualization_type.startsWith("listing__")) {
              const limit = parseInt(
                field.visualization_type.split("__").pop(),
              );
              initialLimitValues[field.id] = limit;
            } else if (field.limit) {
              initialLimitValues[field.id] = field.limit;
            }
          }
        });
        setSelectedFields(initialSelectedFields);
        setVisualizationTypeValues(initialVisualizationTypes);
        setLimitValues(initialLimitValues);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching personal info form:", err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have a company selected or if we need default data
    // For now, we'll not fetch on initial load since we want users to select a company first
    setLoading(false);
  }, []);

  // New useEffect to handle company selection
  useEffect(() => {
    if (selectedCompany) {
      const fetchPersonalInfoForm = async () => {
        try {
          setLoading(true);
          setError(null);
          const response =
            await formLabelService.getPersonalInfoForm(selectedCompany);
          const dashboardData = response.dashboard_fields?.[0];
          const fields = dashboardData?.fields || [];
          const id = dashboardData?.id;
          const extraSettings = dashboardData.extra_settings ?? [];
          const smsValue = dashboardData?.sms ?? false;

          setFormFields(fields);
          setDashboardFieldsId(id);
          if (extraSettings.length > 0) {
            extraSettings.forEach((setting) => {
              Object.entries(setting).forEach(([key, value]) => {
                if (key == "stage_filter") {
                  setIsStageFilterEnabled(value);
                }
                if (key == "total_candidates_score") {
                  setTotalCandidatesScoreEnabled(value);
                }
              });
            });
          }

          // Initialize selectedFields with isChecked values
          const initialSelectedFields = {};
          const initialVisualizationTypes = {};
          const initialLimitValues = {};

          // Initialize social mobility score field from API response
          initialSelectedFields["sms"] = smsValue;
          initialVisualizationTypes["sms"] = null; // Default to no visualization type

          // Initialize total candidates score field from API response
          const totalCandidatesScoreValue =
            dashboardData?.total_candidates_score ?? false;
          initialSelectedFields["total_candidates_score"] =
            totalCandidatesScoreValue;
          initialVisualizationTypes["total_candidates_score"] = null; // Default to no visualization type

          fields.forEach((field) => {
            if (field.isChecked) {
              initialSelectedFields[field.id] = true;
            }
            if (field.visualization_type) {
              initialVisualizationTypes[field.id] = field.visualization_type;
              // Extract limit from visualization type if it's a listing type
              if (field.visualization_type.startsWith("listing__")) {
                const limit = parseInt(
                  field.visualization_type.split("__").pop(),
                );
                initialLimitValues[field.id] = limit;
              } else if (field.limit) {
                initialLimitValues[field.id] = field.limit;
              }
            }
          });
          setSelectedFields(initialSelectedFields);
          setVisualizationTypeValues(initialVisualizationTypes);
          setLimitValues(initialLimitValues);
        } catch (err) {
          setError(err.message);
          console.error("Error fetching personal info form for company:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchPersonalInfoForm();
    } else {
      // When no company is selected, show a proper message
      setFormFields([]);
      setDashboardFieldsId(null);
      setSelectedFields({});
      setVisualizationTypeValues({});
      setLimitValues({});
      setIsStageFilterEnabled(false);
      setError(null);
      setLoading(false);
    }
  }, [selectedCompany]);

  // Save selected company to localStorage whenever it changes
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem(
        "adminDashboardSelectedCompany",
        JSON.stringify(selectedCompany),
      );
    } else {
      localStorage.removeItem("adminDashboardSelectedCompany");
    }
  }, [selectedCompany]);

  // Group fields into triplets
  const fieldTriplets = [];
  for (let i = 0; i < formFields.length; i += 3) {
    fieldTriplets.push(formFields.slice(i, i + 3));
  }

  const handleCheckboxChange = (fieldId) => {
    setSelectedFields((prev) => {
      const newState = {
        ...prev,
        [fieldId]: !prev[fieldId],
      };

      // Console log all checked values
      const checkedValues = Object.entries(newState)
        .filter(([key, value]) => value === true)
        .map(([key, value]) => ({ fieldId: key, checked: value }));

      console.log(" Checked Fields:", checkedValues);

      return newState;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Prepare payload with all fields and their checked status
      const selectedFieldsPayload = {
        fields: formFields.map((field) => {
          const visualizationType = visualizationTypeValues[field.id] || null;
          let limit = 10; // default limit

          // Extract limit from visualization type if it's a listing type
          if (visualizationType && visualizationType.startsWith("listing__")) {
            limit = parseInt(visualizationType.split("__").pop());
          } else if (limitValues[field.id]) {
            limit = limitValues[field.id];
          }

          return {
            id: field.id,
            label: field.label,
            field_type: field.field_type,
            isChecked: selectedFields[field.id] || false,
            visualization_type: visualizationType,
          };
        }),
        sms: selectedFields["sms"] || false,
        extra_settings: [
          {
            stage_filter: isStageFilterEnabled,
            total_candidates_score: totalCandidatesScoreEnabled,
          },
        ],
      };

      // if (!dashboardFieldsId) {
      //   showToast({
      //     message: "Dashboard fields ID not found",
      //     type: "error"
      //   });
      //   return;
      // }

      // Call the createFields API with the dashboard fields ID
      await formLabelService.updateFields(
        selectedCompany,
        selectedFieldsPayload,
      );

      // Show success toast
      showToast({
        message: "Fields saved successfully!",
        type: "success",
      });

      // Remove the reset of selections after successful save
      // setSelectedFields({});
    } catch (error) {
      console.error("Error saving fields:", error);
      // Show error toast
      showToast({
        message: error.message || "Failed to save fields",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    const companyData = companies.find((c) => c.id === selectedCompany);
    const saveAndView = async () => {
      if (selectedCompany) {
        await handleSave();
        localStorage.setItem(
          "selectedViewCompany",
          JSON.stringify(companyData),
        );
        // Open the dashboard preview page in a new tab
        window.open(`/dashboard/${companyData.slug}`, "_blank");
      }
    };
    saveAndView();
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Client Dashboard
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Configure data visibility, visualization types, and feature
                toggles for client-facing dashboards.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-[300px]">
                <Dropdown 
                  options={companies.map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                  value={
                    selectedCompany
                      ? {
                          label: companies.find((c) => c.id === selectedCompany)
                            ?.name,
                          value: selectedCompany,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setSelectedCompany(selected?.value || null)
                  }
                  placeholder="Select Company"
                  icon={
                    <Building className="w-5 h-5 text-foreground flex-shrink-0" />
                  }
                  searchable={true}
                  className="w-full h-[36px]"
                  dropdownWidth="w-[300px]"
                  isLoading={isLoadingCompanies}
                  loadingText="Loading Companies..."
                />
              </div>
              <button
                onClick={handlePreview}
                disabled={!selectedCompany || saving}
                className="bg-primary-dark text-white px-4 h-[36px] rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save & Preview
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Card */}
        <div className="bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] outline outline-1 outline-border p-8 flex flex-col gap-8">
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold text-foreground">
                Personal Form Fields
              </h2>
              <p className="text-muted-foreground text-sm font-normal">
                Select which fields should be visible on the dashboard and how
                they should be visualized.
              </p>
            </div>
            <ToggleSwitch
              label="Stage filter"
              checked={isStageFilterEnabled}
              onChange={() => {
                const newValue = !isStageFilterEnabled;
                setIsStageFilterEnabled(newValue);
                console.log("🔧 Stage Filter Toggled:", newValue);
              }}
            />
          </div>

          {loading && <Loader loadingText="Loading form fields..." />}

          {error && (
            <div className="text-red-500 py-4">
              <p>Error: {error}</p>
            </div>
          )}

          {!loading && !error && !selectedCompany && (
            <div className="text-center py-8">
              <div className="text-muted-foreground mb-4">
                <Building className="w-16 h-16 mx-auto text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                No Company Selected
              </h3>
              <p className="text-muted-foreground">
                Please select a company from the dropdown above to view and
                configure dashboard fields.
              </p>
            </div>
          )}

          {!loading && !error && selectedCompany && formFields.length === 0 && (
            <div className="text-center py-8">
              <div className="text-muted-foreground mb-4">
                <Building className="w-16 h-16 mx-auto text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                No Fields Available
              </h3>
              <p className="text-muted-foreground">
                No dashboard fields found for the selected company.
              </p>
            </div>
          )}

          {!loading && !error && selectedCompany && formFields.length > 0 && (
            <div className="space-y-4">
              {/* Static Checkboxes Row */}
              <div className="flex gap-5">
                {/* Static Social Mobility Question Card */}
                <div className="flex flex-col gap-1 p-4 border rounded-lg w-[335px]">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={selectedFields["sms"] || false}
                      onChange={() => {
                        handleCheckboxChange("sms");
                        console.log(
                          "📊 Social Mobility Score Toggled:",
                          !selectedFields["sms"],
                        );
                      }}
                    />
                    <h4 className="font-medium">Social Mobility Score</h4>
                  </div>
                </div>

                {/* Static Total Candidates Score Question Card */}
                <div className="flex flex-col gap-1 p-4 border rounded-lg w-[335px]">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={totalCandidatesScoreEnabled}
                      onChange={() => {
                        const newValue = !totalCandidatesScoreEnabled;
                        setTotalCandidatesScoreEnabled(newValue);
                        console.log(
                          "📊 Total Candidates Score Toggled:",
                          newValue,
                        );
                      }}
                    />
                    <h4 className="font-medium">Total Candidates Score</h4>
                  </div>
                </div>
              </div>
              <hr />

              {fieldTriplets.map((triplet, tripletIndex) => (
                <div key={tripletIndex} className="grid grid-cols-3 gap-6">
                  {triplet.map((fields) => {
                    const options =
                      fieldDataTypeOptions[fields.label] ||
                      fieldDataTypeOptions["default"];
                    return (
                      <div
                        key={fields.id}
                        className="flex flex-col gap-1 p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input"
                            checked={selectedFields[fields.id] || false}
                            onChange={() => handleCheckboxChange(fields.id)}
                          />
                          <h4 className="font-medium">{fields.label}</h4>
                        </div>

                        {/* limit  */}
                        {/* <div className="flex items-center justify-between gap-3">
                          <h4 className="text-sm font-medium">Limit</h4>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`limit-${fields.id}`}
                                value={10}
                                checked={fields.limit === 10 || fields.limit === undefined}
                                onChange={() => setFormFields(prev => prev.map(f => f.id === fields.id ? {...f, limit: 10} : f))}
                                className="h-[14px] w-[14px]"
                              />
                              <span>10</span>
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`limit-${fields.id}`}
                                value={20}
                                checked={fields.limit === 20}
                                onChange={() => setFormFields(prev => prev.map(f => f.id === fields.id ? {...f, limit: 20} : f))}
                                className="h-[14px] w-[14px]"
                              />
                              <span>20</span>
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`limit-${fields.id}`}
                                value={30}
                                checked={fields.limit === 30}
                                onChange={() => setFormFields(prev => prev.map(f => f.id === fields.id ? {...f, limit: 30} : f))}
                                className="h-[14px] w-[14px]"
                              />
                              <span>30</span>
                            </label>
                          </div>
                        </div> */}
                        {/* HR above dropdown */}
                        <hr className="my-2" />

                        {/* Visualization type dropdown */}
                        <Dropdown
                          options={visualizationOptions}
                          value={
                            visualizationOptions.find(
                              (opt) =>
                                opt.value ===
                                visualizationTypeValues[fields.id],
                            ) || null
                          }
                          onChange={(option) => {
                            const newVisualizationTypes = {
                              ...visualizationTypeValues,
                              [fields.id]: option.value,
                            };
                            setVisualizationTypeValues(newVisualizationTypes);

                            // Console log visualization type changes
                            console.log("🎨 Visualization Type Changed:", {
                              fieldId: fields.id,
                              fieldLabel: fields.label,
                              visualizationType: option.value,
                            });
                            console.log(
                              "📈 All Visualization Types:",
                              newVisualizationTypes,
                            );
                          }}
                          placeholder="Select data type"
                          className=""
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !selectedCompany || formFields.length === 0}
              className="bg-primary-dark text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
